import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function TimelineSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'vertical';

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Horizontal variant ──
    if (variant === 'horizontal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Nuestra historia'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="overflow-x-auto pb-4">
                        <div className="flex items-start gap-0 min-w-max relative">
                            {/* Line */}
                            <div className="absolute top-6 left-0 right-0 h-0.5" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb' }} />
                            {items.map((item, i) => (
                                <div key={i} className={`flex flex-col items-center text-center w-52 px-4 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${200 + i * 150}ms` : '0ms' }}>
                                    {/* Dot */}
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xs font-bold z-10 shadow-lg" style={{ backgroundColor: item.color || s.accent }}>
                                        {item.year}
                                    </div>
                                    <h4 className="font-bold text-sm mt-4" style={{ color: s.text }}>{item.title}</h4>
                                    {item.description && <p className="text-xs mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                    {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-xs font-medium mt-2 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Alternating variant ──
    if (variant === 'alternating') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Nuestra historia'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="relative max-w-4xl mx-auto">
                        {/* Center line */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 hidden md:block" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb' }} />
                        <div className="space-y-12">
                            {items.map((item, i) => {
                                const isLeft = i % 2 === 0;
                                return (
                                    <div key={i} className={`relative flex items-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                        style={{ transitionDelay: visible ? `${200 + i * 150}ms` : '0ms' }}>
                                        {/* Mobile / left content */}
                                        <div className={`w-full md:w-5/12 ${isLeft ? 'md:text-right md:pr-10' : 'md:ml-auto md:pl-10'}`}>
                                            <div className={`${card} p-5`}>
                                                <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: item.color || s.accent }}>{item.year}</span>
                                                <h4 className="font-bold text-base mt-3" style={{ color: s.text }}>{item.title}</h4>
                                                {item.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{item.description}</p>}
                                                {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-xs font-medium mt-2 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                                            </div>
                                        </div>
                                        {/* Center dot */}
                                        <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-4 z-10 hidden md:block" style={{ backgroundColor: item.color || s.accent, borderColor: s.dark ? 'rgba(0,0,0,0.3)' : '#ffffff' }} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Vertical (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Nuestra historia'} subtitle={content.subtitle} config={config} s={s} />
                <div className="relative max-w-2xl mx-auto">
                    {/* Line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb' }} />
                    <div className="space-y-10">
                        {items.map((item, i) => (
                            <div key={i} className={`relative pl-16 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                style={{ transitionDelay: visible ? `${200 + i * 150}ms` : '0ms' }}>
                                {/* Dot */}
                                <div className="absolute left-3.5 top-1 w-5 h-5 rounded-full border-4 z-10" style={{ backgroundColor: item.color || s.accent, borderColor: s.dark ? 'rgba(0,0,0,0.3)' : '#ffffff' }} />
                                <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: item.color || s.accent }}>{item.year}</span>
                                <h4 className="font-bold text-lg mt-2" style={{ color: s.text }}>{item.title}</h4>
                                {item.description && <p className="text-sm mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-2 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
